/* import __COLOCATED_TEMPLATE__ from './users-search.hbs'; */
import { task, timeout, waitForProperty } from 'ember-concurrency';
import { ADMIN_USER_CONTROLLER } from '../controllers/admin/users';
import { on } from '@ember/object/evented';
import EmberObject, { observer, computed } from '@ember/object';
import { A } from '@ember/array';
import { inject as service } from '@ember/service';
import Component from '@ember/component';

const CLASS_NAME = 'SH-c-users-search';

const NULL_OPTION_LABEL = 'Any';

const COLLAPSE_STATES = {
  collapsing: 'collapsing',
  collapsed: 'collapsed',
  expanding: 'expanding',
  expanded: 'expanded',
  removed: 'removed',
};

const SORTS = {
  NAME: 'name',
  NAME_MATCH: 'nameMatch',
};

const USER_TYPES = {
  1: 'Seller',
  2: 'Buyer',
  3: 'Admin',
};

const VETTING_TYPES = {
  1: 'Vetted',
  2: 'Un-vetted',
  3: 'Needs Vetting',
  4: 'Declined With Feedback',
  5: 'Declined Forever',
};

const GLOBAL_FILTERS = [
  ADMIN_USER_CONTROLLER.FILTERS.TYPE,
  ADMIN_USER_CONTROLLER.FILTERS.ID_SLUG_EMAIL,
  ADMIN_USER_CONTROLLER.FILTERS.NAME,
  ADMIN_USER_CONTROLLER.FILTERS.COUNTRY,
  ADMIN_USER_CONTROLLER.FILTERS.DELETION_REQUEST_ONLY,
  ADMIN_USER_CONTROLLER.FILTERS.VERIFIED,
];

const COMPANY_ID_VALUE = `_values.${ADMIN_USER_CONTROLLER.FILTERS.COMPANY_ID}`;

export default Component.extend({
  tagName: '',
  CLASS_NAME,

  ADMIN_USER_CONTROLLER,

  store: service(),

  isDisabled: false,

  onReset: () => {},

  sort: null,

  sortOrder: null,

  setValues: () => {},

  values: {},

  /**
   * Internal
   */

  /**
   * Local copy of _values so we can work on a scratch copy
   * This is set at init() and when reset is requested
   */
  _values: EmberObject.create({}),

  /**
   * Tracks the current state of the search dropdown tray
   * @type {Number}
   */
  _trayState: null,

  /**
   * Value in the input box to be used for type-ahead searching
   * @type {String}
   */
  _companySelectorInput: null,

  _availableCompanies: A([]),

  _availableCountries: A([]),

  _availableLanguages: A([]),

  _availableServices: A([]),

  _buyerRowCollapseModifier: 'removed',

  _sellerRowCollapseModifier: 'removed',

  /**
   * Fixed options
   */
  ratingOptions: [
    {
      key: NULL_OPTION_LABEL,
      model: null,
    },
    ...[1,2,3,4,5].map((key) => ({
      key: key,
      model: key,
    })),
  ],

  userTypeOptions: [
    {
      key: NULL_OPTION_LABEL,
      model: null,
    },
    ...Object.keys(USER_TYPES).map((key) => ({
      key: USER_TYPES[key],
      model: key,
    })),
  ],

  vettingOptions: [
    {
      key: NULL_OPTION_LABEL,
      model: null,
    },
    ...Object.keys(VETTING_TYPES).map((key) => ({
      key: VETTING_TYPES[key],
      model: key,
    })),
  ],

  init() {
    this._super(...arguments);

    this.get('loadCountries').perform();
    this.get('loadLanguages').perform();
    this.get('loadServices').perform();

    this.companyObserver.perform();
  },

  companyObserver: task(function *() {
    let companyId;

    while (true) {
      companyId = this._values[ADMIN_USER_CONTROLLER.FILTERS.COMPANY_ID];
      this.loadCompanies.perform(true);
      yield waitForProperty(this, COMPANY_ID_VALUE, (id) => id !== companyId);
    }
  }),

  valuesChangeObserver: on('init', observer(
    'values',
    function () {
      this.set('_values', EmberObject.create(this.get('values').getProperties(Object.values(ADMIN_USER_CONTROLLER.FILTERS))));
    }
  )),

  userTypeObserver: on('init', observer(
    `_values.${ADMIN_USER_CONTROLLER.FILTERS.TYPE}`,
    function () {
      const currentType = this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.TYPE}`)
        ? parseInt(this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.TYPE}`))
        : null; // Prevent NaN

      if (currentType === this.get('_trayState')) {
        return;
      }

      this.set('_trayState', currentType);

      // Animate the new tray
      this.get('animateTray').perform(currentType);
    }
  )),

  loadCompanies: task(function *(immediate = false) {
    if (!immediate) {
      yield timeout(250);
    }

    const selectedCompanyId = this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.COMPANY_ID}`);

    // If we have a selected ID, load just that one
    if (!!selectedCompanyId) {
      const loadedCompany = this.get('_availableCompanies').findBy('id', selectedCompanyId);
      if (loadedCompany) {
        // Already loaded
        return;
      }

      const company = yield this.get('store').findRecord('company', selectedCompanyId);
      this.set('_availableCompanies', A([company]));
      return;
    }

    // We need to load multiple companies
    const params = {
      'page': 1, // Only need one page of results
      'count': 30,
      'sort': SORTS.NAME,
    };

    if (!!this.get('_companySelectorInput')) {
      params.filter = {
        'name': this.get('_companySelectorInput'),
      };
      params.sort = SORTS.NAME_MATCH;
    }

    this.set('_availableCompanies', A([]));

    const availableCompanies = yield this.get('store').query('company', params);

    this.set('_availableCompanies', availableCompanies);
  }).restartable(),

  loadCountries: task(function *() {
    const availableCountries = yield this.get('store').findAll('country');

    this.set('_availableCountries', availableCountries);
  }),

  loadLanguages: task(function *() {
    const availableLanguages = yield this.get('store').findAll('language');

    this.set('_availableLanguages', availableLanguages);
  }),

  loadServices: task(function *() {
    const availableServices = yield this.get('store').findAll('service');

    this.set('_availableServices', availableServices);
  }),

  selectedCompanyLabel: computed(
    '_availableCompanies.[]',
    '_companySelectorInput',
    `_values.${ADMIN_USER_CONTROLLER.FILTERS.COMPANY_ID}`,
    function() {
      const companySelectorInput = this.get('_companySelectorInput');

      // If we have entered a value, return that
      if (companySelectorInput !== null) {
        return companySelectorInput;
      }

      // Otherwise show the selected company name if any
      const selectedId = this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.COMPANY_ID}`);
      const company = this.get('_availableCompanies').findBy('id', selectedId);

      return company ? company.get('name') : NULL_OPTION_LABEL;
    }
  ),

  selectedCountryLabel: computed(
    '_availableCountries.[]',
    `_values.${ADMIN_USER_CONTROLLER.FILTERS.COUNTRY}`,
    function() {
      const selectedCode = this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.COUNTRY}`);
      const country = this.get('_availableCountries').findBy('code', selectedCode);

      return country ? country.get('name') : NULL_OPTION_LABEL;
    }
  ),

  selectedLanguageLabel: computed(
    '_availableLanguages.[]',
    `_values.${ADMIN_USER_CONTROLLER.FILTERS.HAS_LANGUAGE}`,
    function() {
      const selectedCode = this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.HAS_LANGUAGE}`);
      const language = this.get('_availableLanguages').findBy('code', selectedCode);

      return language ? language.get('name') : NULL_OPTION_LABEL;
    }
  ),

  selectedRatingLabel: computed(
    `_values.${ADMIN_USER_CONTROLLER.FILTERS.MINIMUM_RATING}`,
    function() {
      return this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.MINIMUM_RATING}`) || NULL_OPTION_LABEL;
    }
  ),

  selectedServiceLabel: computed(
    '_availableServices.[]',
    `_values.${ADMIN_USER_CONTROLLER.FILTERS.HAS_SERVICE}`,
    function() {
      const selectedId = this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.HAS_SERVICE}`);
      const service = this.get('_availableServices').findBy('id', selectedId);

      return service ? service.get('name') : NULL_OPTION_LABEL;
    }
  ),

  selectedUserTypeLabel: computed(
    `_values.${ADMIN_USER_CONTROLLER.FILTERS.TYPE}`,
    function() {
      return USER_TYPES[this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.TYPE}`)] || NULL_OPTION_LABEL;
    }
  ),

  selectedVettingLabel: computed(
    `_values.${ADMIN_USER_CONTROLLER.FILTERS.VETTING}`,
    function() {
      return VETTING_TYPES[this.get(`_values.${ADMIN_USER_CONTROLLER.FILTERS.VETTING}`)] || NULL_OPTION_LABEL;
    }
  ),

  /** @type {A<Object>} */
  companyOptions: computed(
    '_availableCompanies.[]',
    function () {
      const availableCompanies = this.get('_availableCompanies') || A([]);

      return A([
        {
          key: NULL_OPTION_LABEL,
          model: null,
        },
        ...availableCompanies.map((company) => ({
          key: company.get('name'),
          model: company.get('id'),
        })),
      ]);
    }
  ),

  /** @type {A<Object>} */
  countryOptions: computed(
    '_availableCountries.[]',
    function () {
      const availableCountries = this.get('_availableCountries') || A([]);

      return A([
        {
          key: NULL_OPTION_LABEL,
          model: null,
        },
        ...availableCountries.map((country) => ({
          key: country.get('name'),
          model: country.get('code'),
        })),
      ]);
    }
  ),

  /** @type {A<Object>} */
  languageOptions: computed(
    '_availableLanguages.[]',
    function () {
      const availableLanguages = this.get('_availableLanguages') || A([]);

      return A([
        {
          key: NULL_OPTION_LABEL,
          model: null,
        },
        ...availableLanguages.map((language) => ({
          key: language.get('name'),
          model: language.get('code'),
        })),
      ]);
    }
  ),

  /** @type {A<Object>} */
  serviceOptions: computed(
    '_availableServices.[]',
    function () {
      const availableServices = this.get('_availableServices') || A([]);

      return A([
        {
          key: NULL_OPTION_LABEL,
          model: null,
        },
        ...availableServices.map((service) => ({
          key: service.get('name'),
          model: service.get('id'),
        })),
      ]);
    }
  ),

  /** @type {A<Object>} */
  sortOptions: computed(
    'sortValue',
    `_values.${ADMIN_USER_CONTROLLER.FILTERS.NAME}`,
  function () {
    const sortValue = this.get('sortValue');

    let options = Object.entries(ADMIN_USER_CONTROLLER.SORT_NAMES);
    if (this._values[ADMIN_USER_CONTROLLER.FILTERS.NAME]) {
      options = options.filter(([key, _]) => key === ADMIN_USER_CONTROLLER.SORTS.BEST_MATCH);
    }

    return options.map(([value, title]) => ({
      title,
      value,
      selected: value === sortValue,
    }));
  }),

  /**
   * Animate our seller/buyer options to the correct view state
   */
  animateTray: task(function *(showTarget) {
    // Hide seller tab if not target
    if (showTarget !== 1) {
      if (this.get('_sellerRowCollapseModifier') !== COLLAPSE_STATES.removed) {
        this.set('_sellerRowCollapseModifier', COLLAPSE_STATES.collapsing);
        yield timeout(600);
        this.set('_sellerRowCollapseModifier', COLLAPSE_STATES.collapsed);
        this.set('_sellerRowCollapseModifier', COLLAPSE_STATES.removed);
      }
    }

    // Hide buyer tab if not target
    if (showTarget !== 2) {
      if (this.get('_buyerRowCollapseModifier') !== COLLAPSE_STATES.removed) {
        this.set('_buyerRowCollapseModifier', COLLAPSE_STATES.collapsing);
        yield timeout(600);
        this.set('_buyerRowCollapseModifier', COLLAPSE_STATES.collapsed);
        this.set('_buyerRowCollapseModifier', COLLAPSE_STATES.removed);
      }
    }

    switch (showTarget) {
      case 1:
        this.set('_sellerRowCollapseModifier', COLLAPSE_STATES.collapsed);
        yield timeout(20);
        this.set('_sellerRowCollapseModifier', COLLAPSE_STATES.expanding);
        yield timeout(600);
        this.set('_sellerRowCollapseModifier', COLLAPSE_STATES.expanded);
        break;
      case 2:
        this.set('_buyerRowCollapseModifier', COLLAPSE_STATES.collapsed);
        yield timeout(20);
        this.set('_buyerRowCollapseModifier', COLLAPSE_STATES.expanding);
        yield timeout(600);
        this.set('_buyerRowCollapseModifier', COLLAPSE_STATES.expanded);
        break;
      default:
        break;
    }
  }),

  /** @inheritDoc */
  actions: {
    onSelectInput(filter, value) {
      switch (filter) {
        case ADMIN_USER_CONTROLLER.FILTERS.COMPANY_ID:
          this.set(`_values.${ADMIN_USER_CONTROLLER.FILTERS.COMPANY_ID}`, null);
          this.set('_companySelectorInput', value);
          this.get('loadCompanies').perform();
          break;
      }
    },

    setFilter(filter, value) {
      const valueOrNull = value === '' ? null : value;

      switch (filter) {
        case ADMIN_USER_CONTROLLER.FILTERS.COMPANY_ID:
          this.set('_companySelectorInput', null); // Clear the input
          break;
        case ADMIN_USER_CONTROLLER.FILTERS.TYPE:
          // Clear all of our non-global options
          this.set('_values', EmberObject.create(this.get('_values').getProperties(GLOBAL_FILTERS)));
          break;
      }

      this.set(`_values.${filter}`, valueOrNull);
    },

    toggleFilter(filter) {
      this.toggleProperty(`_values.${filter}`);
    },

    handleReset() {
      this.set('_values', EmberObject.create({}));

      this.get('onReset')();
    },

    handleSearch(e) {
      e.preventDefault();

      this.get('setValues')(this.get('_values'));
    },
  },
});
